import React, { useEffect, useState, FC } from 'react'; 
import LoginLayout from "../components/LoginLayout";
import moment from "moment";
import { reduxForm, InjectedFormProps, Field as ReduxField } from 'redux-form';
import Button from '@mui/material/Button';
import { email, required } from '../../../components/validation/ValidationUtils';
import { renderTextField } from "../components/LoginForm";
import { Timer } from "../components/Timer";
import { injectIntl, WrappedComponentProps } from 'react-intl';

interface FormValues {
    email: string;
}

export interface CalloutStyle {
    opacity: number;
    margin: string;
    padding: string;
}

type Props = InjectedFormProps<FormValues> & WrappedComponentProps;

const Field = ReduxField as unknown as React.ComponentType<any>;

const PasswordResetRequestPage: FC<Props> = ({ handleSubmit, intl }) => {

    const [calloutStyleState, setCalloutStyleState] = useState<CalloutStyle>({
        opacity: 0,
        margin: '0px',
        padding: '0px'
    });

    const [lastEmailInput, setLastEmailInputState] = useState<number>(0);
    const [submitDisabled, setSubmitDisabledState] = useState<boolean>(false);
    const [errorState, setErrorState] = useState<boolean>(false); 
    const [canRender, setCanRenderState] = useState<boolean>(false);

    const onKeyUp = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSubmit(onSubmit)();
        }
    }

    const onSubmit = async (values: FormValues, _dispatch: any) => {
        try {
            const json = await fetch(`/api/requestResetPw`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values)
            }).then(r => r.json());

            setLastEmailInputState(json);
            setSubmitDisabledState(moment.now() <= json);

        } catch (e) {
            setErrorState(true);
        } finally {
            setCalloutStyleState({ opacity: 1, margin: '20px 0', padding: '20px' });
            setTimeout(() => {
                setCalloutStyleState({ opacity: 0, margin: '0', padding: '0' });
            }, 10000);
        }
    }

    useEffect(() => {
        if (!submitDisabled) {
            fetch('/api/lastPwResetTimestamp')
                .then(response => response.json())
                .then((data: { lastRequest: number | null }) => {
                    if (data.lastRequest !== null) {
                        setLastEmailInputState(data.lastRequest);
                        setSubmitDisabledState(moment.now() <= data.lastRequest);
                    } else {
                        setLastEmailInputState(0);
                        setSubmitDisabledState(false);
                    }
                    setCanRenderState(true);
                })
                .catch(error => {
                    console.error('Error fetching last password reset timestamp:', error);
                    setErrorState(true);
                    setCanRenderState(true);
                });
        }
    }, [submitDisabled]);

    if (!canRender) return null;

    return (
        <LoginLayout>
            <div className="body-inner">
                <div className="card bg-color-light">
                    <div className="card-content theme-light">
                        <section className="logo text-center">
                            <h3 style={{ color: '#0054ff' }}>
                                {intl.formatMessage({ id: 'passwordReset' })}
                            </h3>
                        </section>

                        <form className="form-horizontal" onSubmit={handleSubmit(onSubmit)}>
                            <fieldset>
                                <div className="form-group">
                                    <Field
                                        component={renderTextField}
                                        label={intl.formatMessage({ id: 'user.email' })}
                                        name="email"
                                        margin="normal"
                                        validate={required}
                                        fullWidth
                                        onKeyUp={onKeyUp}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </div>
                            </fieldset>
                            <div className="card-action no-border text-right">
                                <Button type="submit" color="primary" disabled={submitDisabled}>
                                    {submitDisabled 
                                        ? <Timer tock={lastEmailInput} setSubmitDisabled={setSubmitDisabledState} /> 
                                        : intl.formatMessage({ id: 'submit' })}
                                </Button>
                            </div>
                        </form>

                        <div style={{
                            opacity: calloutStyleState.opacity,
                            margin: calloutStyleState.margin,
                            padding: calloutStyleState.padding,
                            transition: `
                                opacity 0.5s ease-in-out,
                                max-height 0.5s ease-in-out,
                                padding 0.5s ease-in-out,
                                margin 0.5s ease-in-out`
                        }} className="callout callout-success no-margin-bottom">
                            <p>
                                {errorState 
                                    ? intl.formatMessage({ id: 'server.error' }) 
                                    : intl.formatMessage({ id: 'emailSent' })}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </LoginLayout>
    );
}

export default injectIntl(
    reduxForm<FormValues, Props>({
        form: 'passwordResetRequest',
        validate: (values: FormValues, props: Props) => {
            const { intl } = props;
            const errors: any = {};
            if (!values.email) {
                errors.email = intl.formatMessage({ id: 'required' });
            } else if (email(values.email)) {
                errors.email = intl.formatMessage({ id: 'invalidEmail' });
            }
            return errors;
        }
    })(PasswordResetRequestPage as any)
);
